// ============================================================================
// 🎯 FORMATAÇÃO DE DATAS
// ============================================================================
export function formatTime(dateString: string | Date): string {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    });
}
/**
 * Formata data para input HTML (YYYY-MM-DD)
 */
export function toDate(date: string | Date): string {
    if (!date) return '';

    const dateObj = new Date(date);
    return dateObj.toISOString().split('T')[0];
}

/**
 * Formata data para exibição (DD/MM/YYYY)
 */
export function toDisplayDate(date: string | Date): string {
    if (!date) return '';

    // Se for uma string ISO com apenas data (00:00:00.000Z), extrair apenas a parte da data
    if (typeof date === 'string' && date.includes('T00:00:00.000Z')) {
        const dateOnly = date.split('T')[0]; // Extrai "2025-10-08" de "2025-10-08T00:00:00.000Z"
        const [year, month, day] = dateOnly.split('-');
        return `${day}/${month}/${year}`;
    }

    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('pt-BR');
}

/**
 * Formata data e hora para exibição (DD/MM/YYYY HH:MM)
 */
export function toDisplayDateTime(date: string | Date): string {
    if (!date) return '';

    const dateObj = new Date(date);
    return dateObj.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

// ============================================================================
// 🎯 FORMATAÇÃO DE HORAS
// ============================================================================

/**
 * Formata hora para input HTML (HH:MM)
 */
export function toTime(time: string): string {
    if (!time) return '';

    // Se já está no formato HH:MM, retorna como está
    if (time.match(/^\d{2}:\d{2}$/)) {
        return time;
    }

    // Se é um timestamp, converte
    const date = new Date(time);
    return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Formata hora para exibição (HH:MM)
 */
export function toDisplayTime(date: string | Date): string {
    if (!date) return '';

    const dateObj = new Date(date);
    return dateObj.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    });
}

// ============================================================================
// 🎯 VALIDAÇÕES DE DATA/HORA
// ============================================================================

/**
 * Verifica se uma string é uma data válida
 */
export function isValidDate(date: string): boolean {
    const dateObj = new Date(date);
    return dateObj instanceof Date && !isNaN(dateObj.getTime());
}

/**
 * Verifica se uma string é uma hora válida (HH:MM)
 */
export function isValidTime(time: string): boolean {
    return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
}

/**
 * Verifica se uma hora final é posterior à hora inicial
 */
export function isTimeAfter(startTime: string, endTime: string): boolean {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    return end > start;
}

// ============================================================================
// 🎯 UTILITÁRIOS DE DATA/HORA
// ============================================================================

/**
 * Obtém a data atual formatada para input
 */
export function getCurrentDate(): string {
    return toDate(new Date());
}

/**
 * Obtém a hora atual formatada para input
 */
export function getCurrentTime(): string {
    return toTime(new Date().toISOString());
}

/**
 * Adiciona dias a uma data
 */
export function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Calcula a diferença em dias entre duas datas
 */
export function getDaysDifference(date1: Date, date2: Date): number {
    const timeDiff = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
}

/**
 * Converte string de tempo (HH:MM) para minutos
 */
export function timeToMinutes(timeString: string): number {
    if (!timeString || typeof timeString !== 'string') return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
}

/**
 * Verifica se o intervalo de tempo é válido considerando o turno
 * @param timeInitial Hora inicial (HH:MM)
 * @param timeFinal Hora final (HH:MM)
 * @param workShift Turno (DAY/NIGHT)
 * @returns true se válido, false caso contrário
 */
export function isValidTimeRange(timeInitial: string, timeFinal: string, workShift?: string): boolean {
    const initialMinutes = timeToMinutes(timeInitial);
    const finalMinutes = timeToMinutes(timeFinal);

    // Se hora final > hora inicial, sempre válido (independente do turno)
    if (finalMinutes > initialMinutes) {
        return true;
    }

    // Se hora final <= hora inicial, só aplica validação de turno se for turno noturno
    if (workShift === 'NIGHT') {
        // Para turno noturno, permite que hora final seja menor que inicial
        // apenas quando cruza a meia-noite (inicial >= 18h e final <= 6h)
        if (initialMinutes >= 1080 && finalMinutes <= 360) {
            return true; // Válido: cruza a meia-noite
        }
        // Se ambos estão no mesmo período (ambos >= 18h ou ambos <= 6h)
        if (initialMinutes >= 1080 && finalMinutes >= 1080) {
            return finalMinutes > initialMinutes; // Ambos após 18h
        }
        if (initialMinutes <= 360 && finalMinutes <= 360) {
            return finalMinutes > initialMinutes; // Ambos antes de 6h
        }
        return false; // Combinação inválida
    }

    // Para turno diurno ou sem turno definido, se hora final <= inicial, é inválido
    return false;
}

/**
 * Calcula a duração em minutos entre dois horários considerando turnos
 * @param timeInitial Hora inicial (HH:MM)
 * @param timeFinal Hora final (HH:MM)
 * @param workShift Turno (DAY/NIGHT)
 * @returns Duração em minutos
 */
export function calculateDuration(timeInitial: string, timeFinal: string, workShift?: string): number {
    const initialMinutes = timeToMinutes(timeInitial);
    const finalMinutes = timeToMinutes(timeFinal);

    // Para turno noturno que cruza a meia-noite
    if (workShift === 'NIGHT' && initialMinutes >= 1080 && finalMinutes <= 360) {
        // Adiciona 24 horas (1440 minutos) ao tempo final
        return finalMinutes + 1440 - initialMinutes;
    }

    // Para outros casos, subtração normal
    return finalMinutes - initialMinutes;
}
